import React, { useEffect } from 'react';
import styled from 'styled-components';
import Footer from './Footer';
import { colors } from '../styles/colors';
import Navigation from './Navigation';

const FullScreenContainer = styled.div`
  width: 100vw;
  min-width: 320px;
  min-height: 100vh;
  min-height: -webkit-fill-available;
  overflow-x: hidden;
  overscroll-behavior-x: none;
  position: relative;
  display: flex;
  flex-direction: column;
  background-color: ${colors.neutral.black};
`;

const HeaderSection = styled.div`
  width: 100%;
  min-height: 120px;
  background-color: ${colors.neutral.darkBrown};
  background-size: 
    2px 2px, 3px 3px, 1px 1px, 4px 4px, 5px 5px, 6px 6px, 
    2px 2px, 7px 7px, 1px 1px, 8px 8px, 2px 2px, 3px 3px;
  background-position: 
    0 0, 1px 1px, 2px 2px, 3px 3px, 4px 4px, 5px 5px,
    6px 6px, 7px 7px, 8px 8px, 9px 9px, 10px 10px, 11px 11px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  box-sizing: border-box;
  position: relative;

  @media (max-width: 1240px) {
    padding: 40px 40px;
  }

  @media (max-width: 800px) {
    min-height: 280px;
    padding: 30px 20px;
  }

  @media (max-width: 480px) {
    min-height: 240px;
    padding: 25px 15px;
  }
`;


const PageTitle = styled.h1`
  font-family: 'saint-andrews-queen', cursive;
  font-size: clamp(7rem, 10vw, 8rem);
  color: ${colors.primary.gold};
  text-align: center;
  padding-top: 100px;
  margin: 0;
  letter-spacing: 2px;
  line-height: 1.2;

  @media (max-width: 800px) {
    font-size: clamp(4rem, 8vw, 6rem);
  }

  @media (max-width: 480px) {
    font-size: clamp(3rem, 6vw, 4.5rem);
  }
`;

const MountainBottom = styled.div`
  position: relative;
  width: 100%;
  height: auto;
  z-index: 50;
  margin-top: -40px; /* Pull mountain up to overlap with section above */
  overflow: hidden;
  
  img {
    width: 100%;
    height: auto;
    display: block;
    min-height: 80px;
    position: relative;
    top: -1px;
    margin-bottom: -1px;
    transform: scaleY(-1) scaleX(1.01); /* Flip upside down and scale slightly wider */
  }

  @media (max-width: 800px) {
    margin-top: -30px;
    img {
      min-height: 60px;
    }
  }

  @media (max-width: 480px) {
    margin-top: -20px;
    img {
      min-height: 40px;
      transform: scaleY(-1) scaleX(1.02); /* Scale slightly more on mobile */
    }
  }
`;

const ContentSection = styled.section<{ 
  backgroundColor?: string; 
  backgroundImage?: string; 
  backgroundOpacity?: number; 
  backgroundSize?: string; 
  backgroundPosition?: string;
  mobileBackgroundPosition?: string;
  tabletBackgroundPosition?: string;
}>`
  width: 100%;
  background-color: ${props => props.backgroundColor || colors.primary.lightTan};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  box-sizing: border-box;
  position: relative;
  z-index: ${props => props.backgroundColor === colors.neutral.darkBrown ? 1 : 10};
  min-height: 400px;
  margin-top: -150px;
  margin-bottom: -150px;
  padding-top: 230px;
  padding-bottom: 230px;

  ${props => props.backgroundImage && `
    &::before {
      content: '';
      position: absolute;
      top: ${props.backgroundColor === colors.primary.lightTan ? '-150px' : '0'};
      left: 0;
      right: 0;
      bottom: ${props.backgroundColor === colors.primary.lightTan ? '-150px' : '0'};
      background-image: url(${props.backgroundImage});
      background-size: ${props.backgroundSize || '70%'};
      background-position: ${props.backgroundPosition || '85% 70%'};
      background-repeat: no-repeat;
      opacity: ${props.backgroundOpacity || 0.3};
      z-index: -1;

      @media (max-width: 800px) {
        top: ${props.backgroundColor === colors.primary.lightTan ? '-80px' : '0'};
        bottom: ${props.backgroundColor === colors.primary.lightTan ? '-80px' : '0'};
        background-size: ${props.backgroundSize || '80%'};
        background-position: ${props.tabletBackgroundPosition || '100% 30%'};
      }

      @media (max-width: 480px) {
        top: ${props.backgroundColor === colors.primary.lightTan ? '-50px' : '0'};
        bottom: ${props.backgroundColor === colors.primary.lightTan ? '-50px' : '0'};
        background-size: ${props.backgroundSize || '100%'};
        background-position: ${props.mobileBackgroundPosition || '100% 20%'};
      }
    }
  `}

  @media (max-width: 1240px) {
    padding: 80px 40px;
    padding-top: 200px;
    padding-bottom: 200px;
  }

  @media (max-width: 800px) {
    padding: 120px 15px;
    min-height: 200px;
    margin-top: -80px;
    margin-bottom: -80px;
  }

  @media (max-width: 480px) {
    padding: 100px 15px;
    min-height: 150px;
    margin-top: -50px;
    margin-bottom: -50px;
  }
`;

const MountainDivider = styled.div<{ flip?: boolean }>`
  position: relative;
  width: 100%;
  height: auto;
  z-index: 50;
  margin: -1px 0;
  overflow: hidden;
  
  img {
    width: 100%;
    height: auto;
    display: block;
    min-height: 80px;
    position: relative;
    bottom: -1px;
    margin-top: -1px;
    transform: ${props => props.flip ? 'scaleY(-1) scaleX(1.01)' : 'scaleX(1.01)'};
  }

  @media (max-width: 800px) {
    img {
      min-height: 60px;
    }
  }

  @media (max-width: 480px) {
    img {
      min-height: 40px;
      transform: ${props => props.flip ? 'scaleY(-1) scaleX(1.02)' : 'scaleX(1.02)'};
    }
  }
`;

const DetailsContainer = styled.div<{ alignLeft?: boolean; alignRight?: boolean }>`
  width: 100%;
  max-width: 1000px;
  display: flex;
  flex-direction: column;
  align-items: ${props => props.alignLeft ? 'flex-start' : props.alignRight ? 'flex-end' : 'center'};
  margin-left: ${props => props.alignLeft ? '-200px' : '0'};
  margin-right: ${props => props.alignRight ? '-200px' : '0'};

  @media (max-width: 1240px) {
    margin-left: ${props => props.alignLeft ? '-50px' : '0'};
    margin-right: ${props => props.alignRight ? '-50px' : '0'};
  }

  @media (max-width: 800px) {
    margin-left: ${props => props.alignLeft ? '-10px' : '0'};
    margin-right: ${props => props.alignRight ? '-10px' : '0'};
  }

  @media (max-width: 480px) {
    margin-left: ${props => props.alignLeft ? '0' : '0'};
    margin-right: ${props => props.alignRight ? '0' : '0'};
  }
`;

const IntroQuote = styled.p`
  font-family: 'GremeryTwo', cursive;
  font-size: clamp(1.3rem, 2.8vw, 1.8rem);
  color: ${colors.neutral.darkOlive};
  text-align: center;
  margin: 0 auto 0 auto;
  max-width: 900px;
  line-height: 1.6;
  font-style: italic;
  letter-spacing: 0.5px;

  @media (max-width: 768px) {
    font-size: 0.95rem;
    margin: 0 auto 0 auto;
  }

  @media (max-width: 480px) {
    font-size: 0.85rem;
    margin: 0 auto 0 auto;
  }
`;

const SectionTitle = styled.h2<{ color?: string; alignLeft?: boolean; alignRight?: boolean }>`
  font-family: 'GremeryTwo', cursive;
  font-size: clamp(2.5rem, 4.5vw, 3.5rem);
  color: ${props => props.color || colors.neutral.darkOlive};
  text-align: ${props => props.alignLeft ? 'left' : props.alignRight ? 'right' : 'center'};
  margin: 0 0 40px 0;
  letter-spacing: 2px;

  @media (max-width: 768px) {
    font-size: 1.5rem;
    margin: 0 0 20px 0;
  }

  @media (max-width: 480px) {
    font-size: 1.25rem;
    margin: 0 0 15px 0;
  }
`;


const DetailItem = styled.div<{ alignLeft?: boolean; alignRight?: boolean; standalone?: boolean; compact?: boolean }>`
  margin-bottom: ${props => props.compact ? '40px' : '150px'};
  display: flex;
  flex-direction: column;
  align-items: ${props => props.alignLeft ? 'flex-start' : props.alignRight ? 'flex-end' : 'center'};
  margin-left: ${props => props.standalone && props.alignLeft ? '-200px' : '0'};
  margin-right: ${props => props.standalone && props.alignRight ? '-200px' : '0'};
  width: 100%;
  max-width: ${props => (props.standalone && (props.alignLeft || props.alignRight)) ? 'calc(100% + 200px)' : '100%'};
  
  &:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 1240px) {
    margin-bottom: ${props => props.compact ? '35px' : '80px'};
    margin-left: ${props => props.standalone && props.alignLeft ? '-50px' : '0'};
    margin-right: ${props => props.standalone && props.alignRight ? '-50px' : '0'};
    max-width: ${props => (props.standalone && (props.alignLeft || props.alignRight)) ? 'calc(100% + 50px)' : '100%'};
  }

  @media (max-width: 800px) {
    margin-bottom: ${props => props.compact ? '30px' : '60px'};
    margin-left: ${props => props.standalone && props.alignLeft ? '-10px' : '0'};
    margin-right: ${props => props.standalone && props.alignRight ? '-10px' : '0'};
    max-width: ${props => (props.standalone && (props.alignLeft || props.alignRight)) ? 'calc(100% + 10px)' : '100%'};
  }

  @media (max-width: 480px) {
    margin-bottom: ${props => props.compact ? '25px' : '40px'};
    margin-left: 0;
    margin-right: 0;
    max-width: 100%;
  }
`;

const DetailLabel = styled.h3<{ color?: string; alignLeft?: boolean; alignRight?: boolean }>`
  font-family: 'GremeryTwo', cursive;
  font-size: clamp(1.8rem, 3vw, 2.5rem);
  color: ${props => props.color || colors.neutral.darkOlive};
  margin: 0 0 20px 0;
  letter-spacing: 1px;
  text-align: ${props => props.alignLeft ? 'left' : props.alignRight ? 'right' : 'center'};

  @media (max-width: 768px) {
    font-size: 1.4rem;
  }

  @media (max-width: 480px) {
    font-size: 1.2rem;
    margin: 0 0 12px 0;
  }
`;

const HotelNameContainer = styled.div<{ alignLeft?: boolean; alignRight?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: ${props => props.alignLeft ? 'flex-start' : props.alignRight ? 'flex-end' : 'center'};
  margin-bottom: 20px;
`;

const HotelNameMain = styled.div<{ color?: string }>`
  font-family: 'saint-andrews-queen', cursive;
  font-size: clamp(3.5rem, 5vw, 5rem);
  color: ${props => props.color || colors.neutral.darkOlive};
  letter-spacing: 2px;

  @media (max-width: 768px) {
    font-size: 3rem;
  }

  @media (max-width: 480px) {
    font-size: 2.5rem;
  }
`;

const HotelNameSubtitle = styled.div<{ color?: string }>`
  font-family: 'GremeryOne', serif;
  font-size: clamp(1.2rem, 2vw, 1.6rem);
  color: ${props => props.color || colors.neutral.darkOlive};
  font-style: italic;
  margin-top: 5px;

  @media (max-width: 768px) {
    font-size: 1rem;
  }

  @media (max-width: 480px) {
    font-size: 0.9rem;
  }
`;

const DetailText = styled.p<{ color?: string; alignLeft?: boolean; alignRight?: boolean }>`
  font-family: 'GremeryTwo', cursive;
  font-size: clamp(1.1rem, 2vw, 1.3rem);
  color: ${props => props.color || colors.neutral.darkBrown};
  margin: 0;
  line-height: 1.7;
  text-align: ${props => props.alignLeft ? 'left' : props.alignRight ? 'right' : 'center'};
  max-width: 800px;

  @media (max-width: 768px) {
    font-size: 0.9rem;
  }

  @media (max-width: 480px) {
    font-size: 0.8rem;
    line-height: 1.5;
  }

  a {
    color: ${props => props.color || colors.neutral.darkOlive};
    text-decoration: underline;
    
    &:hover {
      color: ${colors.primary.gold};
    }
  }
`;

const ImportantNotice = styled.div<{ color?: string; alignRight?: boolean }>`
  font-family: 'GremeryTwo', cursive;
  font-size: clamp(1.1rem, 2vw, 1.3rem);
  color: ${props => props.color || colors.neutral.darkBrown};
  margin-top: 20px;
  line-height: 1.7;
  text-align: ${props => props.alignRight ? 'right' : 'left'};
  max-width: 800px;

  @media (max-width: 768px) {
    font-size: 0.9rem;
  }

  @media (max-width: 480px) {
    font-size: 0.8rem;
    line-height: 1.5;
  }

  strong {
    font-weight: bold;
    color: ${props => props.color || colors.neutral.darkOlive};
    font-size: 1.15em;
  }
`;

const FAQItem = styled.div<{ alignLeft?: boolean }>`
  margin-bottom: 40px;
  display: flex;
  flex-direction: column;
  align-items: ${props => props.alignLeft ? 'flex-start' : 'center'};
  width: 100%;
  max-width: 800px;
  
  &:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 768px) {
    margin-bottom: 30px;
  }

  @media (max-width: 480px) {
    margin-bottom: 25px;
  }
`;

const FAQQuestion = styled.h3<{ color?: string; alignLeft?: boolean }>`
  font-family: 'GremeryTwo', cursive;
  font-size: clamp(1.4rem, 2.5vw, 1.8rem);
  color: ${props => props.color || colors.neutral.darkOlive};
  margin: 0 0 15px 0;
  letter-spacing: 0.5px;
  text-align: ${props => props.alignLeft ? 'left' : 'center'};
  font-weight: 600;

  @media (max-width: 768px) {
    font-size: 1.2rem;
    margin: 0 0 12px 0;
  }

  @media (max-width: 480px) {
    font-size: 1.1rem;
    margin: 0 0 10px 0;
  }
`;

const FAQAnswer = styled.p<{ color?: string; alignLeft?: boolean }>`
  font-family: 'GremeryTwo', cursive;
  font-size: clamp(1.1rem, 2vw, 1.3rem);
  color: ${props => props.color || colors.neutral.darkBrown};
  margin: 0;
  line-height: 1.7;
  text-align: ${props => props.alignLeft ? 'left' : 'center'};

  @media (max-width: 768px) {
    font-size: 0.9rem;
  }

  @media (max-width: 480px) {
    font-size: 0.8rem;
    line-height: 1.5;
  }
`;

const BookingContainer = styled.div<{ alignRight?: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: ${props => props.alignRight ? 'flex-end' : 'flex-start'};
  gap: 15px;
  margin-top: 25px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 12px;
    margin-top: 20px;
  }

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: ${props => props.alignRight ? 'flex-end' : 'flex-start'};
    gap: 10px;
  }
`;

const BookingButtonWrapper = styled.a`
  display: inline-block;
  cursor: pointer;
  transition: opacity 0.3s ease;
  text-decoration: none;
  position: relative;
  width: 200px;

  &:hover {
    opacity: 0.8;
  }

  @media (max-width: 768px) {
    width: 180px;
  }

  @media (max-width: 480px) {
    width: 160px;
  }
`;

const BookingButtonImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
`;

const BookingButtonText = styled.div`
  position: absolute;
  top: 52%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: ${colors.neutral.darkBrown};
  font-size: 1.1rem;
  font-family: 'GremeryOne', serif;
  font-weight: 500;
  letter-spacing: 1px;
  pointer-events: none;
  white-space: nowrap;

  @media (max-width: 768px) {
    font-size: 1rem;
    letter-spacing: 0.5px;
  }
  
  @media (max-width: 480px) {
    font-size: 0.9rem;
    letter-spacing: 0.25px;
  }
`;

const BookingCodeContainer = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-family: 'GremeryTwo', cursive;
  font-size: clamp(1rem, 1.8vw, 1.2rem);
  color: ${colors.neutral.cream};

  @media (max-width: 768px) {
    font-size: 0.95rem;
  }

  @media (max-width: 480px) {
    font-size: 0.9rem;
  }
`;

const BookingCode = styled.span`
  font-family: 'GremeryOne', monospace;
  font-size: clamp(1.1rem, 2vw, 1.3rem);
  color: ${colors.primary.gold};
  font-weight: bold;
  letter-spacing: 1px;

  @media (max-width: 768px) {
    font-size: 1rem;
  }

  @media (max-width: 480px) {
    font-size: 0.95rem;
  }
`;

const HotelImage = styled.img`
  width: 100%;
  max-width: 800px;
  height: auto;
  box-sizing: border-box;
  border-radius: 25px;
  border: 6px solid ${colors.primary.gold};
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.4);
  margin-top: 25px;

  @media (max-width: 768px) {
    max-width: 600px;
    border-radius: 20px;
    border: 5px solid ${colors.primary.gold};
  }

  @media (max-width: 480px) {
    max-width: 100%;
    border-radius: 15px;
    border: 4px solid ${colors.primary.gold};
  }
`;


const TravelDetails: React.FC = () => {
  // Fix for mobile Safari viewport height
  useEffect(() => {
    const setVH = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    setVH();
    window.addEventListener('resize', setVH);
    window.addEventListener('orientationchange', setVH);

    return () => {
      window.removeEventListener('resize', setVH);
      window.removeEventListener('orientationchange', setVH);
    };
  }, []);


  return (
    <FullScreenContainer>
      {/* Brown Header Section with Title */}
      <HeaderSection>
        <Navigation />
        <PageTitle>Details</PageTitle>
      </HeaderSection>
      
      {/* Mountain divider from brown to beige */}
      <MountainBottom>
        <img src="/images/mountains/mountain-soft-top.svg" alt="Mountain divider" />
      </MountainBottom>

      {/* Beige Quote Section */}
      <ContentSection 
        backgroundColor={colors.primary.lightTan}
        backgroundImage="/images/palm_bg_01.png"
        backgroundOpacity={0.3}
        backgroundSize="auto 115%"
        backgroundPosition="85% 10%"
        mobileBackgroundPosition="100% 10%"
        tabletBackgroundPosition="100% 10%"
      >
        <DetailsContainer>
          <IntroQuote>
            We're so excited to celebrate with you in Cabo! Below you'll find everything you need to plan your trip. If you have any questions at all, please don't hesitate to reach out.<br />- E & G
          </IntroQuote>
        </DetailsContainer>
      </ContentSection>

      {/* Mountain divider back to brown */}
      <MountainDivider>
        <img src="/images/mountains/mountain-soft-top.svg" alt="Mountain divider" />
      </MountainDivider>

      {/* Brown Section - Getting There */}
      <ContentSection 
        backgroundColor={colors.neutral.darkBrown}
        backgroundImage="/images/landing_bg.png"
        backgroundOpacity={0.3}
        backgroundSize="60%"
        backgroundPosition="100% 50%"
      >
        <DetailsContainer alignLeft>
          <SectionTitle color={colors.neutral.cream} alignLeft>Getting There</SectionTitle>
          <DetailItem alignLeft>
            <DetailText color={colors.neutral.cream} alignLeft>
              The nearest airport is Los Cabos International Airport (SJD).
              It is about a 30 minute drive to the hotels.
              If possible, book your flights early as this is peak travel season in Cabo.
            </DetailText>
          </DetailItem>
        </DetailsContainer>
      </ContentSection>

      {/* Mountain divider flipped at bottom of Getting There */}
      <MountainBottom>
        <img src="/images/mountains/mountain-soft-top.svg" alt="Mountain divider" />
      </MountainBottom>

      {/* Beige Section - Transportation */}
      <ContentSection 
        backgroundColor={colors.primary.lightTan}
        backgroundImage="/images/post_bg.png"
        backgroundOpacity={0.3}
        backgroundSize="auto 95%"
        backgroundPosition="0% center"
      >
        <DetailsContainer alignRight>
          <SectionTitle alignRight>Ground Transportation</SectionTitle>
          <DetailItem alignRight>
            <DetailText alignRight>
              Details to come.
            </DetailText>
            <ImportantNotice alignRight>
              If you are staying at the Four Seasons, please note that there are <strong>TWO</strong> Four Seasons in Cabo. You will be going to the one in Cabo Del Sol.
            </ImportantNotice>
          </DetailItem>
        </DetailsContainer>
      </ContentSection>

      {/* Mountain divider to brown */}
      <MountainDivider>
        <img src="/images/mountains/mountain-soft-top.svg" alt="Mountain divider" />
      </MountainDivider>

      {/* Brown Section - Accommodations */}
      <ContentSection backgroundColor={colors.neutral.darkBrown}>
        <DetailsContainer>
          <DetailItem alignLeft standalone>
            <SectionTitle color={colors.neutral.cream} alignLeft>Accommodations</SectionTitle>
            <HotelNameContainer alignLeft>
              <HotelNameMain color={colors.neutral.cream}>The Four Seasons</HotelNameMain>
              <HotelNameSubtitle color={colors.neutral.cream}>Cabo Del Sol</HotelNameSubtitle>
            </HotelNameContainer>
            <DetailText color={colors.neutral.cream} alignLeft>
              We will be staying at the Four Seasons Resort at Cabo Del Sol, where all wedding events will be taking place. 
              A limited room block has been reserved at the Four Seasons at a preferred rate.
              The room block is for Thursday, December 3rd through Sunday, December 6th.
              <br />
              If you would like to reserve your stay for additional days, please contact the Four Seasons directly for booking at (855)-281-1098 or email resgroups.cabosanlucas@fourseasons.com.
            </DetailText>
            <HotelImage src="/images/four_seasons_demo.jpg" alt="Four Seasons Resort Los Cabos" />
            <BookingContainer>
              <BookingButtonWrapper href="https://www.fourseasons.com/cabosanlucas/accommodations/" target="_blank" rel="noopener noreferrer">
                <BookingButtonImage src="/images/svgs/funky-book.svg" alt="Book Button" />
                <BookingButtonText>BOOK HERE</BookingButtonText>
              </BookingButtonWrapper>
              <BookingCodeContainer>
                Promo code <BookingCode>261114PRW</BookingCode>
              </BookingCodeContainer>
            </BookingContainer>
          </DetailItem>
          <DetailItem alignRight standalone>
            <HotelNameContainer alignRight>
              <HotelNameMain color={colors.neutral.cream}>Hacienda del Mar</HotelNameMain>
            </HotelNameContainer>
            <DetailText color={colors.neutral.cream} alignRight>
              We also recommend Hacienda del Mar, which is located next door to the Four Seasons.
              There are a variety of different room options at various price points.
            </DetailText>
            <HotelImage src="/images/hacienda_demo.jpg" alt="Hacienda Del Mar" />
            <BookingContainer alignRight>
              <BookingButtonWrapper href="https://www.haciendadelmar.com.mx" target="_blank" rel="noopener noreferrer">
                <BookingButtonImage src="/images/svgs/funky-book.svg" alt="Book Button" />
                <BookingButtonText>BOOK HERE</BookingButtonText>
              </BookingButtonWrapper>
            </BookingContainer>
          </DetailItem>
        </DetailsContainer>
      </ContentSection>

      {/* Mountain divider flipped to beige */}
      <MountainBottom>
        <img src="/images/mountains/mountain-soft-top.svg" alt="Mountain divider" />
      </MountainBottom>

      {/* Beige Section - Travel FAQ */}
      <ContentSection 
        backgroundColor={colors.primary.lightTan}
        backgroundImage="/images/wave_bg.png"
        backgroundOpacity={0.3}
        backgroundSize="cover"
        backgroundPosition="center 45%"
        mobileBackgroundPosition="center 45%"
        tabletBackgroundPosition="center 45%"
      >
        <DetailsContainer alignLeft>
          <SectionTitle alignLeft>Travel FAQ</SectionTitle>
          <FAQItem alignLeft>
            <FAQQuestion alignLeft>Do I need a passport?</FAQQuestion>
            <FAQAnswer alignLeft>
              Yes, a valid passport is required for travel to Mexico. Please make sure your passport is valid for at least 6 months beyond your return date.
            </FAQAnswer>
          </FAQItem>
          <FAQItem alignLeft>
            <FAQQuestion alignLeft>What should I pack?</FAQQuestion>
            <FAQAnswer alignLeft>
              Cabo is warm and sunny year round. Think lightweight clothing, resort wear, and swimwear. Evenings can be cooler, so a light jacket or wrap is recommended.
            </FAQAnswer>
          </FAQItem>
          <FAQItem alignLeft>
            <FAQQuestion alignLeft>What currency should I bring?</FAQQuestion>
            <FAQAnswer alignLeft>
              The Mexican Peso is the local currency, though US Dollars and credit cards are accepted everywhere.
            </FAQAnswer>
          </FAQItem>
          <FAQItem alignLeft>
            <FAQQuestion alignLeft>When should I arrive?</FAQQuestion>
            <FAQAnswer alignLeft>
              In order to attend all the planned events, arrive on Thursday, December 3rd. Otherwise, we are excited to see you as soon as you can be there!
            </FAQAnswer>
          </FAQItem>
        </DetailsContainer>
      </ContentSection>

      <Footer />
    </FullScreenContainer>
  );
};

export default TravelDetails; 